import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Typography,
  Link,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { SUBSCRIPTION_SOURCES, SUBSCRIPTION_PLANS_VENDORS } from "../../constants/admin";
import { useApi } from "../../hooks/useApi";

export default function SubscriptionDialog({
  open,
  onClose,
  editing,
  formData,
  onFormDataChange,
  onSave,
}) {
  const api = useApi();
  const [planModalOpen, setPlanModalOpen] = React.useState(false);
  const [planList, setPlanList] = React.useState([]);
  const [planLoading, setPlanLoading] = React.useState(false);
  const [planError, setPlanError] = React.useState(null);
  const [selectedPlanId, setSelectedPlanId] = React.useState("");
  const [planTypeFilter, setPlanTypeFilter] = React.useState("");
  const [planSearch, setPlanSearch] = React.useState("");
  const [debouncedPlanSearch, setDebouncedPlanSearch] = React.useState("");
  const [autoFillLoading, setAutoFillLoading] = React.useState(false);
  const [autoFillError, setAutoFillError] = React.useState(null);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedPlanSearch(planSearch), 300);
    return () => clearTimeout(t);
  }, [planSearch]);

  const fetchPlans = React.useCallback(async () => {
    setPlanLoading(true);
    setPlanError(null);
    setSelectedPlanId(formData.plan || "");
    try {
      const params = new URLSearchParams();
      params.append("limit", "100");
      params.append("offset", "0");
      if (debouncedPlanSearch?.trim()) {
        params.append("search", debouncedPlanSearch.trim());
      }
      const data = await api.get(`/admin/subscription-plans?${params.toString()}`);
      setPlanList(data.data || []);
    } catch (err) {
      setPlanError(err.message || "Failed to fetch subscription plans");
    } finally {
      setPlanLoading(false);
    }
  }, [api, formData.plan, debouncedPlanSearch]);

  React.useEffect(() => {
    if (planModalOpen) {
      setPlanSearch("");
    }
  }, [planModalOpen]);

  React.useEffect(() => {
    setAutoFillError(null);
  }, [formData.plan]);

  React.useEffect(() => {
    if (planModalOpen) fetchPlans();
  }, [planModalOpen, fetchPlans]);

  React.useEffect(() => {
    if (planModalOpen && formData.source) {
      const match = SUBSCRIPTION_PLANS_VENDORS.find(
        (v) => v.toLowerCase() === (formData.source || "").toLowerCase()
      );
      setPlanTypeFilter(match || "");
    } else if (planModalOpen) {
      setPlanTypeFilter("");
    }
  }, [planModalOpen, formData.source]);

  const filteredPlans = React.useMemo(() => {
    if (!planTypeFilter?.trim()) return planList;
    return planList.filter(
      (p) => (p.vendor || "").toLowerCase() === planTypeFilter.toLowerCase()
    );
  }, [planList, planTypeFilter]);

  const handleApplyPlan = () => {
    if (!selectedPlanId) return;
    onFormDataChange({ ...formData, plan: selectedPlanId });
    setPlanModalOpen(false);
  };

  const handleSelectPlan = (planId) => {
    setSelectedPlanId(planId);
  };

  const handleAutoFillFromPlan = React.useCallback(async () => {
    const planId = formData.plan?.trim();
    if (!planId) return;
    setAutoFillLoading(true);
    setAutoFillError(null);
    try {
      const plan = await api.get(`/admin/subscription-plans/${planId}`);
      const term = (plan.term || "annual").toLowerCase();
      const startDate = formData.start_date || new Date().toISOString().slice(0, 10);
      const start = new Date(startDate);
      let endDate;
      if (term === "monthly") {
        const end = new Date(start);
        end.setMonth(end.getMonth() + 1);
        endDate = end.toISOString().slice(0, 10);
      } else {
        const end = new Date(start);
        end.setFullYear(end.getFullYear() + 1);
        endDate = end.toISOString().slice(0, 10);
      }
      onFormDataChange({
        ...formData,
        start_date: startDate,
        end_date: endDate,
        renewal_date: endDate,
        amount: plan.price != null ? String(plan.price) : formData.amount,
        source: plan.vendor?.trim() || formData.source,
      });
    } catch (err) {
      setAutoFillError(err.message || "Failed to load plan details");
    } finally {
      setAutoFillLoading(false);
    }
  }, [api, formData, onFormDataChange]);

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>{editing ? "Edit Subscription" : "Add Subscription"}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth margin="dense" size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status || "pending"}
                onChange={(e) => onFormDataChange({ ...formData, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="locked">Locked</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              label="Plan"
              value={formData.plan || ""}
              onChange={(e) => onFormDataChange({ ...formData, plan: e.target.value || null })}
              margin="dense"
              size="small"
              placeholder="Subscription plan ID"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setPlanModalOpen(true)}
                      aria-label="Browse plans"
                      edge="end"
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {formData.plan?.trim() && (
              <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleAutoFillFromPlan}
                  disabled={autoFillLoading}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  {autoFillLoading ? (
                    <CircularProgress size={14} />
                  ) : (
                    <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                  )}
                  Auto-fill dates & amount from plan
                </Link>
              </Box>
            )}
            {autoFillError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {autoFillError}
              </Typography>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Plan Legacy"
              value={formData.plan_legacy || ""}
              onChange={(e) => onFormDataChange({ ...formData, plan_legacy: e.target.value })}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Zoho Organization ID"
              value={formData.zoho_organization_id || ""}
              onChange={(e) => onFormDataChange({ ...formData, zoho_organization_id: e.target.value })}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              required
              label="Start Date"
              type="date"
              value={formData.start_date || ""}
              onChange={(e) => onFormDataChange({ ...formData, start_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              required
              label="End Date"
              type="date"
              value={formData.end_date || ""}
              onChange={(e) => onFormDataChange({ ...formData, end_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              required
              label="Renewal Date"
              type="date"
              value={formData.renewal_date || ""}
              onChange={(e) => onFormDataChange({ ...formData, renewal_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Next Billing Date"
              type="date"
              value={formData.next_billing_date || ""}
              onChange={(e) => onFormDataChange({ ...formData, next_billing_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Cancelled Date"
              type="date"
              value={formData.cancelled_date || ""}
              onChange={(e) => onFormDataChange({ ...formData, cancelled_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              required
              label="Amount"
              type="number"
              value={formData.amount || ""}
              onChange={(e) => onFormDataChange({ ...formData, amount: e.target.value })}
              margin="dense"
              size="small"
              inputProps={{ step: "0.01", min: "0" }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.is_recurring || false}
                  onChange={(e) => onFormDataChange({ ...formData, is_recurring: e.target.checked })}
                />
              }
              label="Is Recurring"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.auto_renew || false}
                  onChange={(e) => onFormDataChange({ ...formData, auto_renew: e.target.checked })}
                />
              }
              label="Auto Renew"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Payment System"
              value={formData.payment_system || ""}
              onChange={(e) => onFormDataChange({ ...formData, payment_system: e.target.value })}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Payment Method ID"
              value={formData.payment_method_id || ""}
              onChange={(e) => onFormDataChange({ ...formData, payment_method_id: e.target.value })}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="External Subscription ID"
              value={formData.external_subscription_id || ""}
              onChange={(e) => onFormDataChange({ ...formData, external_subscription_id: e.target.value })}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth margin="dense" size="small" required>
              <InputLabel>Source</InputLabel>
              <Select
                value={formData.source || ""}
                onChange={(e) => onFormDataChange({ ...formData, source: e.target.value || null })}
                label="Source"
              >
                <MenuItem value="">None</MenuItem>
                {[...SUBSCRIPTION_SOURCES]
                  .concat(formData.source && !SUBSCRIPTION_SOURCES.includes(formData.source) ? [formData.source] : [])
                  .map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes || ""}
              onChange={(e) => onFormDataChange({ ...formData, notes: e.target.value })}
              margin="dense"
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Cancellation Reason"
              multiline
              rows={2}
              value={formData.cancellation_reason || ""}
              onChange={(e) => onFormDataChange({ ...formData, cancellation_reason: e.target.value })}
              margin="dense"
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={
            !formData.plan?.trim() ||
            !formData.source?.trim() ||
            !formData.start_date?.trim() ||
            !formData.end_date?.trim() ||
            !formData.renewal_date?.trim() ||
            (formData.amount ?? "").toString().trim() === ""
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>

    {/* Plan Picker Modal */}
    <Dialog
      open={planModalOpen}
      onClose={() => { setPlanModalOpen(false); setPlanError(null); }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: "85vh" } }}
    >
      <DialogTitle>Select Subscription Plan</DialogTitle>
      <DialogContent dividers>
        {planError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {planError}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth size="small" margin="dense" disabled={planLoading}>
              <InputLabel>Type (Vendor)</InputLabel>
              <Select
                value={planTypeFilter}
                onChange={(e) => setPlanTypeFilter(e.target.value)}
                label="Type (Vendor)"
              >
                <MenuItem value="">All</MenuItem>
                {SUBSCRIPTION_PLANS_VENDORS.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              size="small"
              margin="dense"
              label="Search by name"
              placeholder="Type to filter plans..."
              value={planSearch}
              onChange={(e) => setPlanSearch(e.target.value)}
              disabled={planLoading}
            />
          </Grid>
        </Grid>
        <Box sx={{ maxHeight: 320, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1, width: "100%" }}>
          {planLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : filteredPlans.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography color="text.secondary">
                {planList.length === 0
                  ? "No subscription plans found"
                  : "No plans match your filters"}
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {filteredPlans.map((plan) => (
                <ListItemButton
                  key={plan.id}
                  selected={selectedPlanId === plan.id}
                  onClick={() => handleSelectPlan(plan.id)}
                >
                  <ListItemText
                    primary={plan.name || plan.id}
                    secondary={[plan.vendor, plan.term].filter(Boolean).join(" · ") || null}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setPlanModalOpen(false)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleApplyPlan}
          disabled={!selectedPlanId || planLoading}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
